import { useEffect, useRef, useState } from "react";
import { ApiError, NetworkError, api } from "./api/client";
import type {
  ClientConfig,
  GroupPreferences,
  LocationInput,
  Member,
  ResultSet,
} from "./api/types";
import { InviteLink } from "./components/InviteLink";
import { PeopleStep } from "./components/PeopleStep";
import { PreferencesStep } from "./components/PreferencesStep";
import { Results } from "./components/Results";
import { loadGeocodingLibrary } from "./googleMaps";
import { usePageTracking } from "./hooks/usePageTracking";

const DEFAULT_PREFS: GroupPreferences = {
  time_window: { mode: "whole_day" },
  categories: [],
  subcategories: [],
  vibe: null,
  budget: null,
  required_attrs: [],
};

type FlowMode = null | "solo" | "invite";

interface Session {
  id: string;
  token: string;
  inviteToken: string;
}

export default function App() {
  usePageTracking();
  const [config, setConfig] = useState<ClientConfig | null>(null);
  const [flowMode, setFlowMode] = useState<FlowMode>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [prefs, setPrefs] = useState<GroupPreferences>(DEFAULT_PREFS);
  const [results, setResults] = useState<ResultSet | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dark, setDark] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(() => {
    try { return localStorage.getItem("wwm_disclaimer") === "accepted"; } catch { return false; }
  });

  function toggleTheme() {
    const next = !dark;
    setDark(next);
    document.documentElement.setAttribute("data-theme", next ? "dark" : "light");
  }

  useEffect(() => {
    api.config().then((cfg) => {
      setConfig(cfg);
      // Eagerly load the geocoding library so it's ready by the time someone
      // types an address and clicks Add. Without this, the Geocoder class isn't
      // available until later, and free-typed addresses fall through to Nominatim.
      if (cfg.ratings.enabled && cfg.ratings.browser_key) {
        loadGeocodingLibrary(cfg.ratings.browser_key).catch(() => {});
      }
    }).catch(() => setConfig(null));
  }, []);

  function report(err: unknown) {
    if (err instanceof ApiError) setError(err.detail || err.message);
    else if (err instanceof NetworkError) setError("Could not reach the server.");
    else setError("Something went wrong.");
  }

  // Ref survives across renders so sequential onAdd calls within the
  // same submitAll batch always see the session created by the first.
  const sessionRef = useRef<Session | null>(null);

  async function ensureSession(): Promise<Session> {
    if (sessionRef.current) return sessionRef.current;
    const created = await api.createSession();
    const next: Session = {
      id: created.session_id,
      token: created.session_token,
      inviteToken: created.invite_token,
    };
    sessionRef.current = next;
    setSession(next);
    return next;
  }

  async function addMember(name: string, location: LocationInput) {
    setBusy(true);
    setError(null);
    try {
      const active = await ensureSession();
      const member = await api.addMember(active.id, active.token, { name, location });
      setMembers((current) => [...current, member]);
      setResults(null);
    } catch (err) {
      report(err);
    } finally {
      setBusy(false);
    }
  }

  async function removeMember(memberId: string) {
    if (!session) return;
    setBusy(true);
    try {
      await api.removeMember(session.id, session.token, memberId);
      setMembers((current) => current.filter((m) => m.member_id !== memberId));
      // The backend drops stored results when the group changes; mirror that here
      // rather than showing a slate computed for a different set of people.
      setResults(null);
    } catch (err) {
      report(err);
    } finally {
      setBusy(false);
    }
  }

  async function compute() {
    if (!session) return;
    setBusy(true);
    setError(null);
    try {
      await api.setPreferences(session.id, session.token, prefs);
      setResults(await api.compute(session.id, session.token));
    } catch (err) {
      report(err);
    } finally {
      setBusy(false);
    }
  }

  function sendFeedback(venueId: string, workedOut: boolean) {
    void api.sendFeedback(venueId, workedOut, members.length).catch(() => {
      /* feedback is fire-and-forget; a failure is not the user's problem */
    });
  }

  function chooseFlow(mode: FlowMode) {
    setFlowMode(mode);
    // For invite flow, eagerly create the session so we can show the link
    if (mode === "invite") {
      void ensureSession();
    }
  }

  const maxGroupSize = config?.max_group_size ?? 10;
  const ratingsKey = config?.ratings.enabled ? config.ratings.browser_key : null;

  function acceptDisclaimer() {
    setDisclaimerAccepted(true);
    try { localStorage.setItem("wwm_disclaimer", "accepted"); } catch { /* private mode */ }
  }

  if (!disclaimerAccepted) {
    return (
      <main>
        <div className="disclaimer-overlay">
          <div className="disclaimer-card">
            <h1>Where Do We Meet</h1>
            <h2>Terms of Use & Disclaimer</h2>

            <div className="disclaimer-body">
              <p>
                By accessing and using this service ("Where Do We Meet"), you acknowledge
                and agree to the following terms. Please read them carefully before proceeding.
              </p>

              <h3>1. Nature of Service</h3>
              <p>
                This service provides estimated meeting-point suggestions based on
                algorithmically computed travel times and third-party venue data. All
                recommendations are indicative and should be independently verified by
                users before making any decisions or travel plans.
              </p>

              <h3>2. Accuracy Disclaimer</h3>
              <p>
                Travel times, distances, venue information (including but not limited to
                operating hours, accessibility, dietary options, and pricing), and
                geographic data are sourced from third-party providers and may be
                inaccurate, incomplete, or outdated. We make no warranties, express or
                implied, regarding the accuracy, reliability, or completeness of any
                information presented.
              </p>

              <h3>3. Limitation of Liability</h3>
              <p>
                To the fullest extent permitted by applicable law, the operators of this
                service, its affiliates, and contributors shall not be liable for any
                direct, indirect, incidental, consequential, or special damages arising
                from or in connection with your use of this service, including but not
                limited to losses resulting from reliance on venue suggestions, travel
                estimates, or any other information provided herein.
              </p>

              <h3>4. No Professional Advice</h3>
              <p>
                This service does not constitute professional, legal, safety, or
                navigational advice. Users are solely responsible for verifying venue
                suitability, safety, and accessibility before visiting any suggested
                location. Exercise personal judgement and discretion at all times.
              </p>

              <h3>5. Privacy & Data Handling</h3>
              <p>
                Location data entered into this service is used solely for the purpose
                of computing meeting-point suggestions. Session data is ephemeral and
                is automatically deleted within 24 hours. No personal information is
                permanently stored unless you explicitly create an account.
              </p>

              <h3>6. Third-Party Services</h3>
              <p>
                This service relies on third-party APIs including Google Maps Platform
                and OpenStreetMap. Your use of this service is also subject to the terms
                of service and privacy policies of these third-party providers.
              </p>

              <h3>7. Use at Your Own Risk</h3>
              <p>
                You use this service entirely at your own risk. By clicking "I Accept"
                below, you confirm that you have read, understood, and agree to these
                terms, and that you will exercise due diligence and personal discretion
                when acting upon any information provided by this service.
              </p>
            </div>

            <button type="button" className="primary disclaimer-accept" onClick={acceptDisclaimer}>
              I Accept — Continue to Service
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main>
      <button type="button" className="theme-toggle" onClick={toggleTheme} title={dark ? "Light mode" : "Dark mode"}>
        {dark ? "\u2600" : "\u263E"}
      </button>
      <header className="masthead">
        <h1>Where Do We Meet</h1>
        <p>Somewhere fair for everyone. No sign-up, nothing saved.</p>
        {members.length === 0 && flowMode === null && (
          <p className="intro muted small">
            Add your friends' locations and we'll find the fairest meeting point —
            a place everyone can reach in roughly equal time, with real venues to pick from.
          </p>
        )}
      </header>

      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}

      {/* Landing choice — shown only when no flow selected and no members */}
      {flowMode === null && members.length === 0 && (
        <div className="landing-choice">
          <button
            type="button"
            className="choice-card"
            onClick={() => chooseFlow("solo")}
          >
            <span className="choice-title">I know where everyone is</span>
            <span className="choice-desc">Add all locations yourself and find a spot</span>
          </button>
          <button
            type="button"
            className="choice-card"
            onClick={() => chooseFlow("invite")}
          >
            <span className="choice-title">Invite friends to add their location</span>
            <span className="choice-desc">Share a link — each person drops their own pin</span>
          </button>
        </div>
      )}

      {/* Invite link — shown prominently in invite mode before anyone joins */}
      {flowMode === "invite" && session && members.length === 0 && (
        <InviteLink inviteToken={session.inviteToken} />
      )}

      {/* People step — shown once a flow is chosen */}
      {flowMode !== null && (
        <PeopleStep
          members={members}
          maxGroupSize={maxGroupSize}
          busy={busy}
          onAdd={addMember}
          onRemove={removeMember}
          apiKey={ratingsKey}
        />
      )}

      {/* Invite link — shown secondary after members added */}
      {session && members.length > 0 && (
        <InviteLink inviteToken={session.inviteToken} />
      )}

      {members.length >= 2 && (
        <>
          <PreferencesStep prefs={prefs} onChange={setPrefs} disabled={busy} />
          <div className="actions">
            <button type="button" className="primary" onClick={() => void compute()} disabled={busy}>
              {busy ? "Working it out\u2026" : "Find the fair spot"}
            </button>
          </div>
        </>
      )}

      {members.length === 1 && (
        <p className="muted centred">Add at least one more person.</p>
      )}

      {results && (
        <Results
          results={results}
          members={members}
          apiKey={ratingsKey}
          timeRequested={prefs.time_window.mode === "slot"}
          onFeedback={sendFeedback}
        />
      )}

      <footer className="site-footer">
        <p>
          Queries or suggestions?{" "}
          <a
            href="https://wa.me/919972039341"
            target="_blank"
            rel="noopener noreferrer"
          >
            Reach out on WhatsApp
          </a>
        </p>
      </footer>
    </main>
  );
}
